import axios from "axios";

const headersJson = {
    headers: {
        "Content-Type": "application/json",
    },
};

const headersMultipart = () => {
    const token = `Bearer ${localStorage.getItem("access")}`;
    return {
        headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token,
        },
    };
};

const headersJsonAuth = (access = null) => {
    const token = `Bearer ${access || localStorage.getItem("access")}`;
    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: token,
        },
    };
};

const createHeadersJsonPaginated = (headers, page, pageSize) => {
    return {
        ...headers,
        params: {
            page,
            page_size: pageSize,
        },
    };
};

const headersJsonPaginated = (page, pageSize, auth) => {
    const headers = auth ? headersJsonAuth() : headersJson;
    return createHeadersJsonPaginated(headers, page, pageSize);
};

const getHeadersJsonPaginated = (page, pageSize, params, auth = true) => {
    const paginatedHeaders = headersJsonPaginated(page, pageSize, auth);
    return {
        ...paginatedHeaders,
        params: {
            ...paginatedHeaders.params,
            ...params,
        },
    };
};

const getHeadersJsonFilter = (params, auth = true) => {
    const headers = auth ? headersJsonAuth() : headersJson;
    return {
        ...headers,
        params: {
            ...params,
        },
    };
};

const api = process.env.REACT_APP_API;

// Maneja errores de autenticación: limpia el storage y redirige al home
function handleAuthError(error) {
    if (
        error?.response?.status === 401 ||
        error?.response?.status === 403 ||
        error?.response?.data?.code === "token_not_valid"
    ) {
        window.location.href = "/?authError=true";
        localStorage.clear();
        return;
    }
    throw error;
}

export const ApiService = {
    login: async (data) => {
        const result = await axios.post(`${api}/api/token/`, data, headersJson);
        return result;
    },
    me: async (token) => {
        try {
            const result = await axios.get(`${api}/auth/me/`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    register: async (data) => {
        try {
            const result = await axios.post(`${api}/auth/register/`, data, headersJson);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    resetPassword: async (data) => {
        try {
            const result = await axios.post(`${api}/auth/reset-password/`, data, headersJsonAuth());
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    resetPasswordSa: async (data) => {
        try {
            const result = await axios.post(
                `${api}/auth/reset-password-sa/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    completeRegisterGoogle: async (data, access) => {
        try {
            const result = await axios.post(
                `${api}/auth/complete-register-google/`,
                data,
                headersJsonAuth(access)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    loginGoogle: async (data = {}) => {
        const result = await axios.post(
            `${api}/auth/google/`,
            data,
            headersJson
        );
        return result;
    },
    getFilterUsers: async (page = 1, pageSize = 10, params) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params);
            const result = await axios.get(`${api}/auth/users/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getProvinces: async () => {
        try {
            const result = await axios.get(`${api}/institutional/provinces/`, headersJson);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getLocations: async (page = 1, pageSize = 10, params, auth = false) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(`${api}/institutional/locations/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getRoles: async () => {
        try {
            const result = await axios.get(`${api}/auth/roles/`, headersJsonAuth());
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getEstablishments: async (page = 1, pageSize = 10, params, auth = true) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(`${api}/institutional/establishment/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getUserEstablishments: async (userId) => {
        try {
            const result = await axios.get(
                `${api}/institutional/establishment/?user=${userId}`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getUserEsetablishmentRoles: async (userId, establishmentId) => {
        try {
            const result = await axios.get(
                `${api}/auth/roles/?user=${userId}&establishment=${establishmentId}`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    updateUserRoles: async (userId, data) => {
        try {
            const result = await axios.put(
                `${api}/auth/users/${userId}/update-role/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    requestUserRoleEstablishment: async (data) => {
        try {
            const result = await axios.put(
                `${api}/auth/user-role-establishments/request-user-role-establishment/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    setEstablishmentManagers: async (establishmentId, data) => {
        try {
            const result = await axios.put(
                `${api}/institutional/establishment/${establishmentId}/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    uploadFile: async (data) => {
        try {
            const result = await axios.post(
                `${api}/activity/upload-file`,
                data,
                headersMultipart()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    deleteActivity: async (idActivity) => {
        try {
            const result = await axios.delete(
                `${api}/activity/activities/${idActivity}/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getRedditGraph: async (params) => {
        try {
            const result = await axios.get(
                `${api}/flow-activity/reddit/word-frequency/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getRedditGraphUserComments: async (params) => {
        try {
            const result = await axios.get(
                `${api}/flow-activity/reddit/user-comments/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getCourses: async (page = 1, pageSize = 10, params, auth = true) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(`${api}/institutional/courses/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getCourse: async (id) => {
        try {
            const result = await axios.get(
                `${api}/institutional/courses/${id}/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getCourseFromId: async (id) => {
        try {
            const result = await axios.get(
                `${api}/institutional/courses/${id}/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    createCourse: async (data) => {
        try {
            const result = await axios.post(
                `${api}/institutional/courses/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getSubjects: async (page = 1, pageSize = 10, params, auth = true) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(`${api}/institutional/subjects/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    joinCourse: async (data) => {
        try {
            const result = await axios.post(
                `${api}/institutional/user-course-requests/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getUserCourseRequests: async (page = 1, pageSize = 10, params, auth = true) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(
                `${api}/institutional/user-course-requests/`,
                filterParams
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    pendingNotification: async (params) => {
        const result = await axios.get(
            `${api}/institutional/notifications/`,
            getHeadersJsonFilter(params)
        );
        return result;
    },
    createActivity: async (data) => {
        try {
            const result = await axios.post(`${api}/activity/activities/`, data, headersJsonAuth());
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    cloneActivity: async (data, idActivity) => {
        try {
            const result = await axios.post(
                `${api}/activity/activities/${idActivity}/clone/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    createSubjectCourseActivity: async (data) => {
        try {
            const result = await axios.post(
                `${api}/activity/subject-course-activity/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getSubjectCourseActivities: async () => {
        try {
            const result = await axios.get(
                `${api}/activity/subject-course-activity/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getSubjectCourseActivity: async (id) => {
        try {
            const result = await axios.get(
                `${api}/activity/subject-course-activity/${id}/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDataGraphBasic: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/get-data-graph-basic/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getValuesForHeader: async (id, params = {}) => {
        try {
            const result = await axios.get(
                `${api}/flow-activity/student-activity/${id}/values-for-header/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDataGraphColumn: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/get-data-graph-group-by/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDataGraphMap: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/get-data-graph-map/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDataGraph: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/get-data-graph/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDataGraphBetweenness: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/get-data-graph-betweenness/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDataWordCloud: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/get-data-word-cloud/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    createStudentActivityResolution: async (data = {}) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity-resolution/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getStudentActivityResolutions: async (params) => {
        try {
            const result = await axios.get(
                `${api}/flow-activity/student-activity-resolution/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    addQuestion: async (id, data = {}) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/add-question/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    qualify: async (data = {}, id) => {
        try {
            const result = await axios.post(
                `${api}/flow-activity/student-activity/${id}/qualify/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    addAnswer: async (data = {}) => {
        try {
            const result = await axios.post(
                `${api}/activity/activity-question/add-answer/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    seeQuestion: async (idQuestion) => {
        try {
            const result = await axios.post(
                `${api}/activity/activity-question/${idQuestion}/see-question/`,
                {},
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getActivities: async (page = 1, pageSize = 10, params, auth = true) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(`${api}/activity/activities/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getActivity: async (id) => {
        try {
            const result = await axios.get(`${api}/activity/activities/${id}/`, headersJsonAuth());
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getStudentActivities: async (params) => {
        try {
            const result = await axios.get(
                `${api}/flow-activity/student-activity/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getStudentActivity: async (id, params) => {
        try {
            const result = await axios.get(
                `${api}/flow-activity/student-activity/${id}/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getGraphs: async () => {
        try {
            const result = await axios.get(`${api}/flow-activity/graph/`, headersJsonAuth());
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDynamicDataSources: async () => {
        try {
            const result = await axios.get(
                `${api}/activity/dynamic-data-source/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getStaticDataSources: async (page = 1, pageSize = 10, params, auth = true) => {
        try {
            const filterParams = getHeadersJsonPaginated(page, pageSize, params, auth);
            const result = await axios.get(`${api}/activity/static-data-source/`, filterParams);
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getDatasetsCloneCreate: async (params) => {
        try {
            const result = await axios.get(
                `${api}/activity/static-data-source/get-data-clone-create/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    downloadStaticDataSourcePdf: async (fileName) => {
        try {
            const result = await axios.get(
                `${api}/activity/static-data-source/download/${fileName}/`,
                {
                    ...headersJsonAuth(),
                    responseType: "blob",
                }
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    downloadStaticDataSource: async (fileName) => {
        try {
            const result = await axios.get(
                `${api}/activity/static-data-source/download/${fileName}/`,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    patchStaticDataSource: async (id, data) => {
        try {
            const result = await axios.patch(
                `${api}/activity/static-data-source/${id}/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    patchActivity: async (id, data) => {
        try {
            const result = await axios.patch(
                `${api}/activity/activities/${id}/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    getUserRoleEstablishments: async (params) => {
        try {
            const result = await axios.get(
                `${api}/auth/user-role-establishments/`,
                getHeadersJsonFilter(params)
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    acceptOrRejectRole: async (ureId, data) => {
        try {
            const result = await axios.put(
                `${api}/auth/user-role-establishments/${ureId}/accept-or-reject/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    acceptOrRejectCourse: async (ucreId, data) => {
        try {
            const result = await axios.put(
                `${api}/institutional/user-course-requests/${ucreId}/accept-or-reject/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    createSubject: async (data) => {
        try {
            const result = await axios.post(
                `${api}/institutional/subjects/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
    updateSubject: async (data, subjectId) => {
        try {
            const result = await axios.put(
                `${api}/institutional/subjects/${subjectId}/`,
                data,
                headersJsonAuth()
            );
            return result;
        } catch (error) {
            handleAuthError(error);
        }
    },
};
